import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemedStyles } from '../../theme/useThemedStyles';
import { formatDate as formatDisplayDate } from '../../utils/dateFormat';
import { isDefaultAppointmentReasonName } from '../calendar/appointmentReason';

interface TreatmentStep {
  id?: number;
  stepNumber: number;
  name: string;
  description?: string;
  cost: number;
  completed: boolean;
  completedAt?: string;
}

interface AuditLog {
  timestamp: string;
  action: string;
  performedBy?: string;
  fieldName?: string;
  oldValue?: any;
  newValue?: any;
}

interface TreatmentDetailsProps {
  id: number;
  name: string;
  cost: number;
  totalCost: number;
  cashPaid: number;
  reservationCreditApplied: number;
  totalPaid: number;
  totalDue: number;
  status: string;
  isCompleted: boolean;
  isFullyPaid: boolean;
  isMultiStep: boolean;
  steps?: TreatmentStep[];
  createdAt?: string;
  createdBy?: string;
  completedAt?: string;
  statusChangedBy?: string;
  statusChangedAt?: string;
  auditLogs?: AuditLog[];
  onStatusChange?: (status: string) => void;
  onMarkComplete?: () => void;
  onMarkIncomplete?: () => void;
  onViewSteps?: (treatmentId: number) => void;
  onEditStep?: (treatmentId: number, step: TreatmentStep) => void;
  onDeleteStep?: (treatmentId: number, stepId: number) => void;
  onViewAudit?: (treatmentId: number) => void;
}

const card: React.CSSProperties = {
  background: '#1e293b',
  borderRadius: 12,
  padding: 16,
  border: '1px solid #334155',
  color: '#f1f5f9'
};

const badge: React.CSSProperties = {
  display: 'inline-block',
  padding: '4px 8px',
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.5
};

const statusColors: Record<string, string> = {
  in_progress: '#0ea5e9',
  completed: '#10b981',
  archived: '#6b7280',
  with_debt: '#dc2626'
};

const progressBar: React.CSSProperties = {
  width: '100%',
  height: 8,
  background: '#0f172a',
  borderRadius: 4,
  overflow: 'hidden',
  marginTop: 4
};

const progressFill: (percent: number) => React.CSSProperties = (percent) => ({
  height: '100%',
  background: `hsl(${percent * 120}, 84%, 50%)`,
  width: `${percent}%`,
  transition: 'width 0.3s ease'
});

const btn: React.CSSProperties = {
  padding: '6px 12px',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  transitionDuration: '200ms'
};

export const TreatmentCard: React.FC<TreatmentDetailsProps> = ({
  id,
  name,
  cost,
  totalCost,
  cashPaid,
  reservationCreditApplied,
  totalPaid,
  totalDue,
  status,
  isCompleted,
  isFullyPaid,
  isMultiStep,
  steps = [],
  createdAt,
  createdBy,
  completedAt,
  statusChangedBy,
  statusChangedAt,
  onStatusChange,
  onMarkComplete,
  onMarkIncomplete,
  onViewSteps,
  onViewAudit
}) => {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const [showAudit, setShowAudit] = useState(false);
  const paymentPercent = totalCost > 0 ? (totalPaid / totalCost) * 100 : 0;
  const stepPercent = steps.length > 0 
    ? (steps.filter(s => s.completed).length / steps.length) * 100 
    : 0;

  const completedSteps = steps.filter(s => s.completed).length;

  const formatDate = (date?: string) => {
    return formatDisplayDate(date, '');
  };

  return (
    <div style={{ ...card, ...theme.card, padding: 16, borderRadius: 12 }}>
      {/* Header Row */}
      <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: '0 0 6px 0', fontSize: 16, fontWeight: 600 }}>{name}</h3>
          <div className="responsive-row" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {isDefaultAppointmentReasonName(name) && (
              <span style={{ ...badge, background: '#6366f1', color: '#fff' }}>{t('treatmentsUi.newPatientConsultation', 'New patient consultation')}</span>
            )}
            <span 
              style={{
                ...badge,
                background: statusColors[status] || statusColors.in_progress,
                color: '#fff'
              }}
            >
              {status === 'in_progress' ? t('treatmentsUi.inProgress') : status === 'completed' ? t('treatmentsUi.completed') : status === 'archived' ? t('treatmentsUi.archived') : status === 'with_debt' ? t('treatmentsUi.withDebt') : status.replace('_', ' ')}
            </span>
            {isCompleted && (
              <span style={{ ...badge, background: '#10b981', color: '#fff' }}>✓ {t('treatmentsUi.complete')}</span>
            )}
            {isFullyPaid && (
              <span style={{ ...badge, background: '#10b981', color: '#fff' }}>✓ {t('treatmentsUi.paid')}</span>
            )}
            {totalDue > 0 && (
              <span style={{ ...badge, background: '#dc2626', color: '#fff' }}>💰 {t('treatmentsUi.due')}</span>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 14, color: theme.muted, marginBottom: 4 }}>{t('treatmentsUi.cost')}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>Bs {totalCost.toFixed(2)}</div>
        </div>
      </div>

      <div style={{ borderBottom: `1px solid ${theme.border}`, marginBottom: 12, paddingBottom: 12 }}>
        {/* Payment Status */}
        <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>{t('treatmentsUi.cashPaid', 'Cash paid')}</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#10b981' }}>Bs {cashPaid.toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>{t('treatmentsUi.reservationCredit', 'Reservation credit')}</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#0ea5e9' }}>Bs {reservationCreditApplied.toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>{t('treatmentsUi.totalApplied', 'Total applied')}</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#10b981' }}>Bs {totalPaid.toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>{t('treatmentsUi.due')}</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: totalDue > 0 ? '#ef4444' : '#10b981' }}>Bs {totalDue.toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>{t('treatmentsUi.percentPaid')}</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: paymentPercent === 100 ? '#10b981' : '#0ea5e9' }}>
              {paymentPercent.toFixed(0)}%
            </div>
          </div>
        </div>
        <div style={progressBar}>
          <div style={progressFill(paymentPercent)} />
        </div>
      </div>

      {/* Step Progress */}
      {isMultiStep && (
        <div style={{ borderBottom: `1px solid ${theme.border}`, marginBottom: 12, paddingBottom: 12 }}>
          <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: theme.muted }}>
              {t('treatmentsUi.stepProgress')}: {completedSteps}/{steps.length}
            </div>
            {onViewSteps && (
              <button
                onClick={() => onViewSteps(id)}
                style={{ ...btn, background: '#0ea5e9', color: '#fff' }}
              >
                {t('treatmentsUi.viewSteps')}
              </button>
            )}
          </div>
          <div style={progressBar}>
            <div style={progressFill(stepPercent)} />
          </div>
          {steps.length > 0 && (
            <div style={{ marginTop: 8, display: 'grid', gap: 4 }}>
              {steps.slice(0, 3).map((step) => (
                <div className="responsive-row" key={step.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  <span style={{ color: step.completed ? '#10b981' : '#6b7280' }}>
                    {step.completed ? '✓' : '○'} {step.name}
                  </span>
                  <span style={{ color: theme.muted }}>Bs {step.cost.toFixed(2)}</span>
                </div>
              ))}
              {steps.length > 3 && (
                <div style={{ color: theme.muted, fontSize: 12 }}>+{steps.length - 3} {t('treatmentsUi.moreSteps')}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="responsive-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {!isCompleted && onMarkComplete && (
          <button
            onClick={onMarkComplete}
            style={{ ...btn, background: '#10b981', color: '#fff' }}
          >
            {t('treatmentsUi.markComplete')}
          </button>
        )}
        {isCompleted && onMarkIncomplete && (
          <button
            onClick={onMarkIncomplete}
            style={{ ...btn, background: '#f59e0b', color: '#000' }}
          >
            {t('treatmentsUi.markIncomplete')}
          </button>
        )}
        {onStatusChange && (
          <select
            onChange={(e) => onStatusChange(e.target.value)}
            defaultValue={status}
            style={{
              ...btn,
              background: theme.surfaceAlt,
              color: theme.text,
              padding: '6px 8px',
              textTransform: 'capitalize'
            }}
          >
            <option value="in_progress">{t('treatmentsUi.inProgress')}</option>
            <option value="completed">{t('treatmentsUi.completed')}</option>
            <option value="archived">{t('treatmentsUi.archived')}</option>
            <option value="with_debt">{t('treatmentsUi.withDebt')}</option>
          </select>
        )}
        {onViewAudit && (
          <button
            onClick={() => setShowAudit(!showAudit)}
            style={{ ...btn, background: '#6b7280', color: '#fff', marginLeft: 'auto' }}
          >
            {showAudit ? t('treatmentsUi.hide') : t('treatmentsUi.viewAudit')}
          </button>
        )}
      </div>

      {/* Audit Trail */}
      {showAudit && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #334155' }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#94a3b8', marginBottom: 8 }}>
            {t('treatmentsUi.recentActivity')}
          </div>
          <div style={{ display: 'grid', gap: 6, maxHeight: 200, overflow: 'auto' }}>
            {createdAt && (
              <div style={{ fontSize: 12, color: '#cbd5e1' }}>
                <span style={{ color: '#94a3b8' }}>{t('treatmentsUi.created')}</span> {formatDate(createdAt)} by {createdBy || '—'}
              </div>
            )}
            {isCompleted && completedAt && (
              <div style={{ fontSize: 12, color: '#cbd5e1' }}>
                <span style={{ color: '#94a3b8' }}>{t('treatmentsUi.completed')}</span> {formatDate(completedAt)} by {statusChangedBy || '—'}
              </div>
            )}
            {statusChangedAt && status !== 'in_progress' && (
              <div style={{ fontSize: 12, color: '#cbd5e1' }}>
                <span style={{ color: '#94a3b8' }}>{t('treatmentsUi.statusChanged')}</span> {formatDate(statusChangedAt)} by {statusChangedBy || '—'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer Info */}
      <div className="responsive-row" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #334155', fontSize: 11, color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
        <span>ID: {id}</span>
        <span>{isMultiStep ? t('treatmentsUi.multiStepTreatment') : t('treatmentsUi.simpleTreatment')}</span>
      </div>
    </div>
  );
};

export default TreatmentCard;
